import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';

import type { AvailableTransition, Issue, Resolution } from '@tasks/contracts';

import {
  deleteIssue,
  duplicateIssue,
  getAvailableTransitions,
  getIssue,
  transitionIssue,
  updateIssue,
} from '../../api/issues';
import { queryKeys } from '../../api/queryKeys';
import { listComponents } from '../../api/components';
import { listSprints } from '../../api/sprints';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Skeleton } from '../../components/Skeleton';
import { Tabs, type TabItem } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { useProjectMembers } from '../../hooks/useProject';
import { IssueAttachmentsTab } from './IssueAttachmentsTab';
import { IssueCommentsTab } from './IssueCommentsTab';
import { IssueDescriptionTab } from './IssueDescriptionTab';
import { IssueHistoryTab } from './IssueHistoryTab';
import { IssuePanelHeader } from './IssuePanelHeader';
import { IssuePropertiesPanel } from './IssuePropertiesPanel';
import { IssueWatchers } from './IssueWatchers';
import { TransitionDialog } from './TransitionDialog';
import { SummaryEditor } from './SummaryEditor';
import styles from './IssuePanel.module.css';

type TabValue = 'description' | 'comments' | 'history' | 'attachments';

export type IssuePanelProps = {
  readonly issueKey: string | null;
  readonly projectKey: string;
  readonly onClose: () => void;
  /** `page` — отдельная страница задачи, `drawer` — панель поверх контента. */
  readonly variant?: 'drawer' | 'page';
};

/**
 * Панель задачи (docs/06 §3.2): вкладки, инлайн-редактирование, переходы,
 * присутствие. В панели состояние синхронизировано с `?issue=KEY`; на странице
 * задачу задаёт сам маршрут `/projects/:key/issues/:issueKey`.
 */
export function IssuePanel({ issueKey, projectKey, onClose, variant = 'drawer' }: IssuePanelProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabValue>('description');
  const [pendingTransition, setPendingTransition] = useState<AvailableTransition | null>(null);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const members = useProjectMembers(projectKey);

  const issueQuery = useQuery({
    queryKey: queryKeys.issues.detail(issueKey ?? ''),
    queryFn: ({ signal }) => getIssue(issueKey as string, signal),
    enabled: Boolean(issueKey),
  });

  const transitionsQuery = useQuery({
    queryKey: queryKeys.issues.transitions(issueKey ?? ''),
    queryFn: ({ signal }) => getAvailableTransitions(issueKey as string, signal),
    enabled: Boolean(issueKey),
  });

  const sprintsQuery = useQuery({
    queryKey: queryKeys.projects.sprints(projectKey, 'all'),
    queryFn: ({ signal }) => listSprints(projectKey, undefined, signal),
    enabled: projectKey.length > 0,
  });

  // Справочник компонентов: выбор идёт из него, а не свободным вводом —
  // компонент нельзя создать опечаткой из задачи (docs/13 §7).
  const componentsQuery = useQuery({
    queryKey: queryKeys.projects.components(projectKey),
    queryFn: ({ signal }) => listComponents(projectKey, { signal }),
    enabled: projectKey.length > 0,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.issues.detail(issueKey ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.issues.transitions(issueKey ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
    // Панель открывается и с персональной доски (E18): её карточки тоже устарели.
    void queryClient.invalidateQueries({ queryKey: queryKeys.personalBoard.root });
  }, [issueKey, projectKey, queryClient]);

  const patchMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) => updateIssue(issueKey as string, patch),
    onMutate: async (patch) => {
      // Оптимистично: значение меняется сразу, откат — в onError.
      const previous = queryClient.getQueryData<Issue>(queryKeys.issues.detail(issueKey ?? ''));
      if (previous) {
        queryClient.setQueryData<Issue>(queryKeys.issues.detail(issueKey ?? ''), {
          ...previous,
          ...(patch as Partial<Issue>),
        });
      }
      return { previous };
    },
    onError: (cause: unknown, _patch, context) => {
      const restored = (context as { previous?: Issue } | undefined)?.previous;
      if (restored) {
        queryClient.setQueryData(queryKeys.issues.detail(issueKey ?? ''), restored);
      }
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
    onSuccess: invalidate,
  });

  const transitionMutation = useMutation({
    mutationFn: (variables: { readonly toStatusId: string; readonly resolution: Resolution | null }) =>
      transitionIssue(issueKey as string, {
        toStatusId: variables.toStatusId,
        resolution: variables.resolution,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKeys.issues.detail(issueKey ?? ''), response.issue);
      setPendingTransition(null);
      invalidate();
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => duplicateIssue(issueKey as string, true),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.personalBoard.root });
      toast.success(`Создана копия ${created.key}`);
      onClose();
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteIssue(issueKey as string, true),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.issues.detail(issueKey ?? '') });
      setDeleteOpen(false);
      onClose();
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.personalBoard.root });
      toast.success('Задача удалена');
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const tabs = useMemo<readonly TabItem<TabValue>[]>(
    () => [
      { value: 'description', label: 'Описание' },
      { value: 'comments', label: 'Комментарии', count: issueQuery.data?.commentsCount },
      { value: 'history', label: 'История' },
      { value: 'attachments', label: 'Вложения', count: issueQuery.data?.attachmentsCount },
    ],
    [issueQuery.data?.attachmentsCount, issueQuery.data?.commentsCount],
  );

  const issue = issueQuery.data ?? null;
  const isPage = variant === 'page';
  // Канонический адрес задачи: на него ведёт «Копировать ссылку».
  const pageHref = issueKey === null ? '' : `/projects/${projectKey}/issues/${issueKey}`;

  const header = issue ? (
    <IssuePanelHeader
      issue={issue}
      transitions={transitionsQuery.data ?? []}
      viewers={[]}
      onTransition={(transition) => {
        if (transition.requireResolution) {
          setPendingTransition(transition);
          return;
        }
        transitionMutation.mutate({ toStatusId: transition.toStatus.id, resolution: null });
      }}
      onDuplicate={() => duplicateMutation.mutate()}
      onOpenPage={isPage ? undefined : () => navigate(pageHref)}
      onCopyLink={() => {
        void navigator.clipboard.writeText(new URL(pageHref, window.location.origin).toString());
        toast.success('Ссылка скопирована');
      }}
      onDelete={() => setDeleteOpen(true)}
    />
  ) : (
    <span>{issueKey}</span>
  );

  const content = issueQuery.isPending ? (
    <div className={styles['loading']}>
      <Skeleton variant="text" count={2} />
      <Skeleton variant="card" count={2} />
    </div>
  ) : issueQuery.isError || !issue ? (
    <EmptyState
      icon={<span>!</span>}
      title="Задача не найдена"
      description={humanizeError(issueQuery.error).message}
      action={
        <Button variant="primary" onClick={() => void issueQuery.refetch()}>
          Повторить
        </Button>
      }
    />
  ) : (
    <div className={styles['body']}>
      <SummaryEditor
        value={issue.summary}
        onSave={(summary) => {
          if (summary !== issue.summary) {
            patchMutation.mutate({ summary });
          }
        }}
      />
      <Tabs items={tabs} value={tab} onChange={setTab} ariaLabel="Разделы задачи" />
      <div className={styles['content']}>
        <div className={styles['main']}>
          <div className={styles['tabBody']}>
            {tab === 'description' ? (
              <IssueDescriptionTab
                issue={issue}
                canEdit
                isSaving={patchMutation.isPending}
                onSave={(description) => patchMutation.mutate({ description })}
              />
            ) : null}
            {tab === 'comments' ? <IssueCommentsTab issueKey={issue.key} /> : null}
            {tab === 'history' ? <IssueHistoryTab issueKey={issue.key} /> : null}
            {tab === 'attachments' ? <IssueAttachmentsTab issueKey={issue.key} /> : null}
          </div>
        </div>
        <div className={styles['side']}>
          <IssuePropertiesPanel
            issue={issue}
            members={members}
            sprints={sprintsQuery.data ?? []}
            components={componentsQuery.data ?? []}
            canEdit
            onPatch={(patch) => patchMutation.mutate(patch)}
            onAddLabel={(label) => patchMutation.mutate({ labels: [...issue.labels, label] })}
            onRemoveLabel={(label) =>
              patchMutation.mutate({ labels: issue.labels.filter((item) => item !== label) })
            }
          />
          <IssueWatchers issueKey={issue.key} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isPage ? (
        <div className={styles['page']} data-testid="issue-page">
          <header className={styles['pageHeader']}>
            <div className={styles['pageHeaderContent']}>{header}</div>
            {/* Крестик на том же месте, что и в компактной панели. */}
            <IconButton label="Закрыть" icon={<X size={18} />} onClick={onClose} />
          </header>
          {content}
        </div>
      ) : (
        <Drawer
          open={Boolean(issueKey)}
          onClose={onClose}
          title={issueKey ?? 'Задача'}
          width={560}
          testId="issue-panel"
          header={header}
        >
          {content}
        </Drawer>
      )}

      <TransitionDialog
        open={pendingTransition !== null}
        transition={pendingTransition}
        isPending={transitionMutation.isPending}
        onClose={() => setPendingTransition(null)}
        onConfirm={(resolution) => {
          if (pendingTransition) {
            transitionMutation.mutate({ toStatusId: pendingTransition.toStatus.id, resolution });
          }
        }}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title="Удалить задачу?"
        description="Задача будет скрыта. Действие записывается в аудит."
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

/** Заголовок задачи: инлайн-редактирование по клику (docs/06 §3.2). */
